import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@rsos/components/capstone/base';
import { useFlag } from '@rsos/flags';
import { useCurrentConversation } from '@rsos/reduxDataStore/src/features/alerts/hooks';
import { useSelectedAlert } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import TalkJSChatbox from '../TalkJSChatbox';
import TalkJSChatboxNext from '../TalkJSChatbox/TalkJSChatboxNext';
import AcceptDeclineAlert from './AcceptDeclineAlert';
import {
  AlertChatBoxContainer,
  AlertActions,
  TalkJSContainer,
  CHAT_HEADER_HEIGHT,
} from './AlertChatBox.styles';
import RemoveAlert from './RemoveAlert';
import { shouldShowAlertActions, shouldShowRemoveAlert } from './helpers';

/**
 * Display an alert chat box using TalkJS when a user accepts an alert. The user can communicate
 * with a user in the Central Station application.
 * @param {String} chatBoxHeight - Current chat box height, the value changes if a user adjusts the split pane height.
 * @param {Function} onRemoveAlertFromQueue - Determine if the alert should be removed from the queue.
 * @param {Function} onShowDeclinePane - Determine if the alert decline options pane should be displayed.
 */
const AlertChatBox = ({
  chatBoxHeight,
  isChatBoxVisible,
  onRemoveAlertFromQueue,
  onShowDeclinePane,
  hasPrivilegedChat,
}) => {
  const currentAlert = useSelectedAlert();

  const alertConversation = useCurrentConversation();

  const selectedAlertID = currentAlert?.alert_id;

  const alertStatus = currentAlert?.status?.name;

  const isSupplementalOnly = currentAlert?.supplemental_only;

  const alertConversationID = alertConversation?.conversation?.id;

  const alertActionsRef = useRef();
  const removeAlertRef = useRef();

  const [showAlertActions, setShowAlertActions] = useState(
    shouldShowAlertActions(alertStatus),
  );

  const defaultAlertActionsHeight = hasPrivilegedChat
    ? `${CHAT_HEADER_HEIGHT}px`
    : '0px';
  const additionalHeight = hasPrivilegedChat ? CHAT_HEADER_HEIGHT : 0;

  const [alertActionsHeight, setAlertActionsHeight] = useState(
    defaultAlertActionsHeight,
  );

  const [showRemoveAlert, setShowRemoveAlert] = useState(
    shouldShowRemoveAlert(alertStatus, isSupplementalOnly),
  );

  // Updates the visibility of alert actions such as accpet, decline, and remove alert.
  useEffect(() => {
    if (selectedAlertID) {
      setShowAlertActions(shouldShowAlertActions(alertStatus));

      setShowRemoveAlert(
        shouldShowRemoveAlert(alertStatus, isSupplementalOnly),
      );
    }
  }, [alertStatus, isChatBoxVisible, isSupplementalOnly, selectedAlertID]);

  // Updates alertActions height to ensure both TalkJS and alert actions are visible on the page.
  useEffect(() => {
    if (selectedAlertID) {
      if (showAlertActions) {
        const currentRef = showRemoveAlert ? removeAlertRef : alertActionsRef;
        setAlertActionsHeight(
          `${currentRef.current.clientHeight + additionalHeight}px`,
        );
      } else {
        setAlertActionsHeight(defaultAlertActionsHeight);
      }
    }
  }, [
    additionalHeight,
    selectedAlertID,
    showAlertActions,
    showRemoveAlert,
    defaultAlertActionsHeight,
  ]);

  // Hide decline pane and show remove alert when alert status is canceled, declined, or timed out.
  useEffect(() => {
    if (showRemoveAlert) {
      onShowDeclinePane(false);
    }
  }, [onShowDeclinePane, showRemoveAlert]);

  const hasTalkJSImprovements = useFlag('talkjs_improvements');

  return (
    <AlertChatBoxContainer isChatBoxVisible={isChatBoxVisible}>
      {selectedAlertID && alertConversationID && (
        <TalkJSContainer
          chatBoxHeight={chatBoxHeight}
          alertActionsHeight={alertActionsHeight}
          isChatBoxVisible={isChatBoxVisible}
        >
          {hasTalkJSImprovements ? (
            <TalkJSChatboxNext instanceID="alerts" />
          ) : (
            <TalkJSChatbox />
          )}
        </TalkJSContainer>
      )}
      {showAlertActions && (
        <AlertActions
          alertStatus={alertStatus}
          showRemoveAlert={showRemoveAlert}
          ref={showRemoveAlert ? removeAlertRef : alertActionsRef}
        >
          <Box padding="10px">
            {showRemoveAlert ? (
              <RemoveAlert onRemoveAlertFromQueue={onRemoveAlertFromQueue} />
            ) : (
              <AcceptDeclineAlert onShowDeclinePane={onShowDeclinePane} />
            )}
          </Box>
        </AlertActions>
      )}
    </AlertChatBoxContainer>
  );
};

AlertChatBox.propTypes = {
  chatBoxHeight: PropTypes.string.isRequired,
  onRemoveAlertFromQueue: PropTypes.func.isRequired,
  onShowDeclinePane: PropTypes.func.isRequired,
  isChatBoxVisible: PropTypes.bool,
  hasPrivilegedChat: PropTypes.bool,
};

export default AlertChatBox;
